import * as Yup from "yup";
import { Job, VehicleType } from "../../generated/nest-graphql";
import { PreJobCheckListV2FormValues } from "./PreJobCheckListV2Dialog";
import { PartialShallow } from "lodash";
import { fleetJobTypes } from "../../lib/constants";

const numericStringSchema = Yup.string().matches(/^[0-9]\d*(\.\d+)?$/, "Must be a valid number");

const brakeFluidSchema = Yup.object().shape({
  ppm: numericStringSchema.required("Required"),
  level: Yup.string().required("Required"),
});

const coreBrakeFluidSchema = Yup.object().shape({
  colorGrade: Yup.string().required("Required"),
  level: Yup.string().required("Required"),
});

const inspectedServiceSchema = Yup.object().shape(
  {
    included: Yup.boolean().optional(),
    colorGrade: Yup.string().when("included", {
      is: (included) => !!included,
      then: Yup.string().required("Required"),
      otherwise: Yup.string().optional(),
    }),
    notes: Yup.string().optional(),
  },
  [["included", "included"]]
);

const padsSchema = Yup.object().shape({
  innerPadThickness: numericStringSchema.required("Required"),
  outerPadThickness: numericStringSchema.required("Required"),
  condition: Yup.array().of(Yup.string()).required("Required").min(1, "Required"),
});

const tireTreadDepthSchema = Yup.object().shape({
  frontDepth: numericStringSchema.when("withoutTreadDepth", {
    is: (withoutTreadDepth) => withoutTreadDepth,
    then: numericStringSchema.nullable(),
    otherwise: numericStringSchema.required(),
  }),
  rearDepth: numericStringSchema.when("withoutTreadDepth", {
    is: (withoutTreadDepth) => withoutTreadDepth,
    then: numericStringSchema.nullable(),
    otherwise: numericStringSchema.required(),
  }),
  withoutTreadDepth: Yup.boolean(),
});

const corePadsSchema = Yup.object().shape({
  padsThickness: numericStringSchema.required("Required"),
  condition: Yup.array().of(Yup.string()).required("Required").min(1, "Required"),
});

const fleetPadsSchema = Yup.object().shape({
  driverSide: numericStringSchema.required("Required"),
  passengerSide: numericStringSchema.required("Required"),
  condition: Yup.array().of(Yup.string()).required("Required").min(1, "Required"),
});

const rotorsSchema = Yup.object().shape({
  thicknessInches: numericStringSchema.required("Required"),
});

const coreRotorsSchema = Yup.object().shape({
  condition: Yup.array().of(Yup.string()).required("Required").min(1, "Required"),
});

const shoesSchema = Yup.object().shape({
  colorGrade: Yup.string().required("Required"),
  condition: Yup.array().of(Yup.string()).required("Required").min(1, "Required"),
});

const drumsSchema = Yup.object().shape({
  colorGrade: Yup.string().required("Required"),
  condition: Yup.array().of(Yup.string()).required("Required").min(1, "Required"),
});

export const checkListV2Schema = Yup.object().shape(
  {
    odometer: numericStringSchema.required("Required"),
    brakeFluid: brakeFluidSchema.required("Required"),
    frontPads: padsSchema.required("Required"),
    frontRotors: rotorsSchema.required("Required"),
    usesRearShoesAndDrums: Yup.boolean().optional(),
    rearPads: Yup.object().when("usesRearShoesAndDrums", {
      is: (usesRearShoesAndDrums) => !usesRearShoesAndDrums,
      then: padsSchema.required("Required"),
      otherwise: Yup.object().nullable(),
    }),
    rearRotors: Yup.object().when("usesRearShoesAndDrums", {
      is: (usesRearShoesAndDrums) => !usesRearShoesAndDrums,
      then: rotorsSchema.required("Required"),
      otherwise: Yup.object().nullable(),
    }),
    rearShoes: Yup.object().when("usesRearShoesAndDrums", {
      is: (usesRearShoesAndDrums) => !!usesRearShoesAndDrums,
      then: shoesSchema,
      otherwise: Yup.object().nullable(),
    }),
    rearDrums: Yup.object().when("usesRearShoesAndDrums", {
      is: (usesRearShoesAndDrums) => !!usesRearShoesAndDrums,
      then: drumsSchema,
      otherwise: Yup.object().nullable(),
    }),
    privateNotes: Yup.string().optional(),
    inspectedServices: Yup.object().shape({
      frontLeftCaliper: inspectedServiceSchema,
      frontRightCaliper: inspectedServiceSchema,
      rearLeftCaliper: inspectedServiceSchema,
      rearRightCaliper: inspectedServiceSchema,
      frontLeftHubAssemblyBearing: inspectedServiceSchema,
      frontRightHubAssemblyBearing: inspectedServiceSchema,
      rearLeftHubAssemblyBearing: inspectedServiceSchema,
      rearRightHubAssemblyBearing: inspectedServiceSchema,
    }),
    withoutCCA: Yup.boolean(),
  },
  [
    ["frontPads", "frontPads"],
    ["usesRearShoesAndDrums", "usesRearShoesAndDrums"],
  ]
);

export const coreCheckListV2Schema = Yup.object().shape(
  {
    odometer: numericStringSchema.required("Required"),
    brakeFluid: coreBrakeFluidSchema.required("Required"),
    frontPads: corePadsSchema.required("Required"),
    frontRotors: coreRotorsSchema.required("Required"),
    usesRearShoesAndDrums: Yup.boolean().optional(),
    rearPads: Yup.object().when("usesRearShoesAndDrums", {
      is: (usesRearShoesAndDrums) => !usesRearShoesAndDrums,
      then: corePadsSchema.required("Required"),
      otherwise: Yup.object().nullable(),
    }),
    rearRotors: Yup.object().when("usesRearShoesAndDrums", {
      is: (usesRearShoesAndDrums) => !usesRearShoesAndDrums,
      then: coreRotorsSchema.required("Required"),
      otherwise: Yup.object().nullable(),
    }),
    rearShoes: Yup.object().when("usesRearShoesAndDrums", {
      is: (usesRearShoesAndDrums) => !!usesRearShoesAndDrums,
      then: shoesSchema,
      otherwise: Yup.object().nullable(),
    }),
    rearDrums: Yup.object().when("usesRearShoesAndDrums", {
      is: (usesRearShoesAndDrums) => !!usesRearShoesAndDrums,
      then: drumsSchema,
      otherwise: Yup.object().nullable(),
    }),
    privateNotes: Yup.string().optional(),
    inspectedServices: Yup.object().shape({
      caliper: inspectedServiceSchema,
      hubAssemblyBearing: inspectedServiceSchema,
    }),
    withoutCCA: Yup.boolean(),
  },
  [
    ["frontPads", "frontPads"],
    ["usesRearShoesAndDrums", "usesRearShoesAndDrums"],
  ]
);

export const fleetCheckListV2Schema = Yup.object().shape(
  {
    odometer: numericStringSchema.required("Required"),
    brakeFluid: coreBrakeFluidSchema.required("Required"),
    frontPads: fleetPadsSchema.required("Required"),
    frontRotors: coreRotorsSchema.required("Required"),
    usesRearShoesAndDrums: Yup.boolean().optional(),
    tireTreadDepth: tireTreadDepthSchema.required("Required"),
    rearPads: Yup.object().when("usesRearShoesAndDrums", {
      is: (usesRearShoesAndDrums) => !usesRearShoesAndDrums,
      then: fleetPadsSchema.required("Required"),
      otherwise: Yup.object().nullable(),
    }),
    rearRotors: Yup.object().when("usesRearShoesAndDrums", {
      is: (usesRearShoesAndDrums) => !usesRearShoesAndDrums,
      then: coreRotorsSchema.required("Required"),
      otherwise: Yup.object().nullable(),
    }),
    rearShoes: Yup.object().when("usesRearShoesAndDrums", {
      is: (usesRearShoesAndDrums) => !!usesRearShoesAndDrums,
      then: shoesSchema,
      otherwise: Yup.object().nullable(),
    }),
    rearDrums: Yup.object().when("usesRearShoesAndDrums", {
      is: (usesRearShoesAndDrums) => !!usesRearShoesAndDrums,
      then: drumsSchema,
      otherwise: Yup.object().nullable(),
    }),
    privateNotes: Yup.string().optional(),
    inspectedServices: Yup.object().shape({
      caliper: inspectedServiceSchema,
      hubAssemblyBearing: inspectedServiceSchema,
    }),
    withoutCCA: Yup.boolean(),
  },
  [
    ["frontPads", "frontPads"],
    ["usesRearShoesAndDrums", "usesRearShoesAndDrums"],
  ]
);

export const getValidationV2Schema = (job: Job, vehicle: VehicleType) => {
  const isVioc = job.type === "Vioc Inspection";
  const isFleet = fleetJobTypes.includes(job.type);
  const isInternalMaintenance = job.type === "Internal Van Maintenance";
  // const coldCrankingAmps = !vehicle.CCA
  //   ? Yup.number().nullable()
  //   : Yup.number().when("withoutCCA", {
  //       is: (withoutCCA) => withoutCCA,
  //       then: Yup.number().nullable(),
  //       otherwise: Yup.number().required(),
  //     });
  const batteryHealth = Yup.number()
    .max(100, "Please enter Battery Health as a percentage value less than or equal to 100")
    .when("withoutBatteryHealth", {
      is: (withoutBatteryHealth) => withoutBatteryHealth,
      then: Yup.number().nullable(),
      otherwise: Yup.number().required(),
    });

  let mainFields: Yup.ObjectSchema<PartialShallow<PreJobCheckListV2FormValues>> = coreCheckListV2Schema;

  if (isVioc) {
    mainFields = checkListV2Schema;
  } else if (isFleet || isInternalMaintenance) {
    mainFields = fleetCheckListV2Schema;
  }
  return isVioc
    ? mainFields
    : mainFields.shape({
        batteryHealth,
      });
};
